import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@clay-run/glaze/tabs';
import { Text } from '@clay-run/glaze/text';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../../_shared/hooks';
import {
  COMING_SOON_TABS,
  PLAY_FLOW_STEPS,
  parsePlayFlowRungParam,
  parsePlayFlowStep,
  playFlowStepParam,
  type PlayFlowStepId,
} from '../../play-flow-step';
import { flowContextKey, type FlowContext } from '../../play-flow-context';
import { clearMembersPanelCollapse } from '../../members-panel-collapse-store';
import { clearDispatchLogFilters } from '../../dispatch-log-filter-store';
import { clearReviewCards } from '../../review-cards-store';
import { clearMemberTests } from '../../member-test-store';
import { clearReviewSelection } from '../../review-selection-store';
import { clearPlaybookDraft } from '../../playbook-draft-store';
import { clearRungScope } from '../../rung-scope-store';
import { clearSequencePreview } from '../../sequence-preview-store';
import { clearLadderAdoptionSignal } from '../../ladder-adoption-signal';
import { clearGeneratedCopyReviews } from '../../generated-copy-store';
import { closeGenerateSession } from '../../generate-with-ai-store';
import { closeUsageSidebarForSeedChange } from '../../usage-sidebar-store';
import { clearSequenceCopyDrafts } from '../../sequence-copy-draft-store';
import { clearWaterfallSaveStore } from '../../waterfall-save-store';
import { formatEventDate } from '../../dates';
import {
  recordFlowSession,
  shouldClearFlowStores,
  updateFlowReturnHref,
  useFlowSessionStore,
} from '../../flow-session-store';
import { renamePlaybook } from '../../mutations';
import { libraryQueryKey, playbookDetailQueryKey, playsQueryKey } from '../../query-keys';
import { mergePlaybookNames, playDisplayName } from '../../plays-logic';
import type { PlaybooksLibraryResponse, PlaybooksPlaybookDetailResponse, PlaybooksPlaysResponse } from '../../schemas';
import { usePlaybooksLibrary } from '../../use-playbooks-library';
import { useLauncherCampaign, useTriggerCampaignMemberSync } from '../../launcher-api';
import {
  beginMemberSync,
  memberSyncPhase,
  newMemberSyncMountKey,
  selectMemberSyncEntry,
  useMemberSyncStore,
} from '../../member-sync-store';
import { usePlayRowActions, usePlaysForCampaign } from '../plays-actions';
import { invalidatePlayLists, usePlaysLibrary } from '../../use-plays-library';
import { KebabMenu } from '../KebabMenu';
import { NameDrawerField } from '../NameDrawerField';
import { PushDrawer } from '../PushDrawer';
import { UnsavedWorkGuard, useHasUnsavedWork } from '../UnsavedWorkGuard';
import { AudienceRulesStep } from './AudienceRulesStep';
import { MembersPanel } from './MembersPanel';
import { QualifyStep } from './QualifyStep';
import { ReviewStep } from './ReviewStep';
import { SequencesStep } from './SequencesStep';

/**
 * Launcher V2 A2 U5 — the play-flow shell mounted at `v2/play/:playId`.
 *
 * A freely-navigable 3-step Glaze `Tabs` stepper (Qualify / Sequences /
 * Review, plan Key Decision 1) with the active step held in `?step=` URL
 * state — see `play-flow-step.ts` for the parse/serialize logic this
 * component drives. No modals anywhere in this tree; the only side panels
 * are `PushDrawer`, and none render directly from this shell (nothing here
 * is a destructive confirm).
 *
 * Covers BOTH the existing-Play and new-Play cases (Key Decision 1): a
 * brand-new Play's id is minted server-side by a real, persisted
 * `mutations.createPlay` call (`CreatePlaySidebar`, `POST /v2/plays`) and
 * routed here exactly like an existing Play's id — this shell does not
 * distinguish them, `QualifyStep` does (see its Play-resolution doc
 * comment) because A1 has no `GET /v2/plays/:id` to resolve one from the
 * other at this level.
 *
 * `campaignSfdcId` rides on the URL as its own query param — the same
 * "route state via query param" idiom `?step=` uses — because A1 exposes no
 * endpoint to resolve a Play's campaign from its id alone (see
 * `PlaybooksHome`'s link-construction comment, the first place this gap
 * was hit). Both `PlaybooksHome`'s "Open" links and its `createPlay` submit
 * handler already carry it.
 *
 * All three steps are real: `QualifyStep` and `SequencesStep` (U5), and
 * `ReviewStep`. The review step's Send/Save controls are gated presentation
 * logic over `mutations.ts`'s stubs — see `ReviewStep`'s own doc comment.
 *
 * ── MEMBERS PANEL (PR B follow-up U5, R5): `MembersPanel` used to mount ONCE
 * here, outside the `Tabs`/`TabPanels` tree, so it never unmounted on a step
 * switch — that structural placement was what made "persists across step
 * switches" true. Spencer's QA asked for it inside each step's left column
 * instead (next to `ContextCard`/`CoverageCard` on Qualify, the rail cards on
 * Sequences), so this shell now builds the element ONCE and hands it to each
 * step as a `membersPanel` render-slot prop; each step places it. Persistence
 * survives the resulting unmount/remount on tab switches (Glaze `Tabs`
 * unmounts inactive panels) because every piece of the panel's state is
 * external — scope in `rung-scope-store`, data in React Query's cache — never
 * component-local `useState` on `MembersPanel` itself (see its own header
 * comment). The missing-`campaignSfdcId` error band stays here at shell
 * level, unchanged.
 *
 * terra-qyt.14 (U9) — generalized to mount against EITHER doorway
 * (`/play/:playId` or `/playbook/:playbookId`) via the `context: FlowContext`
 * prop `index.tsx`'s two keyed route wrappers build
 * (`flowContextFromRoute`/`flowContextKey`, `play-flow-context.ts`). This
 * shell narrows on `context.kind` for identity (`playId`/`playbookId`) but
 * still reads `campaignSfdcId` off the URL's own `?campaignSfdcId=` in BOTH
 * modes — a playbook route's seed campaign rides the SAME query param a play
 * route's required one does, so every existing `campaignSfdcId`-driven
 * derivation below (the members panel, the steps themselves) works
 * unmodified in either mode. The two identity-shaped bands ("Missing
 * playId"/"Missing campaignSfdcId") are PLAY-ONLY: a playbook has no
 * `playId` to be missing, and an unseeded playbook is an expected, supported
 * state (U10 adds the seed-picker card), not an error.
 */
export interface PlayFlowShellProps {
  context: FlowContext;
}

export function PlayFlowShell({ context }: PlayFlowShellProps) {
  const playId = context.kind === 'play' ? context.playId : undefined;
  const playbookId = context.kind === 'playbook' ? context.playbookId : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  // U7 (E3) — the exact href the "Unsaved edits" pill navigates back to.
  const location = useLocation();
  const campaignSfdcId = searchParams.get('campaignSfdcId') ?? '';
  const step = parsePlayFlowStep(searchParams.get('step'));
  const queryClient = useQueryClient();
  const { apiFetch } = useApi();
  const [renamePlaybookOpen, setRenamePlaybookOpen] = useState(false);
  const [savingPlaybookRename, setSavingPlaybookRename] = useState(false);
  /**
   * H2 — the "Where is this used" deep link's `&rung=` param
   * (`UsagePlacesList`, H1): a rung id to preselect in whichever step this
   * Play opens onto. `null` when absent, or when the value doesn't resolve
   * to a real rung — `SequencesStep`/`QualifyStep` own that lookup against
   * their own `railRungs`/ladder (they're the ones with data loaded), so
   * this shell only parses the raw param and hands it down unvalidated.
   */
  const preselectRungId = parsePlayFlowRungParam(searchParams.get('rung'));

  /**
   * Live member resync whenever a `campaignSfdcId` is bound
   * (plans/2026-09-17-playbooks-live-member-sync-plan.md) — Spencer's
   * decision covers BOTH doorways: an opened/created play, and a playbook's
   * seed-campaign pick (`handleSeedChange`). The plan's original "play mode
   * only" recommendation is superseded; the queue-pressure cost of a seed
   * pick firing an undeduplicated live sync on the concurrency-1
   * `launcher-member-sync` queue is accepted as-is (open question 2 in the
   * plan) rather than debounced.
   *
   * Exactly once per REAL mount: `beginMemberSync` claims the campaign's
   * entry in `member-sync-store` and returns `false` when a sync is already
   * in flight, so StrictMode's dev setup→cleanup→setup fires ONE request
   * (the second setup joins the first). No lifetime ref anywhere — see the
   * store's own doc for why state, not a ref, is the guard here.
   *
   * `mountKey` is per mount (`useState` initializer, kept across the
   * StrictMode double-invoke, fresh on a real remount). `memberSyncPhase`
   * treats an entry settled for a DIFFERENT key as still `'syncing'`, which is
   * what keeps a re-opened play (or a freshly re-picked seed campaign) gated
   * until ITS sync settles — the previous roster is never the first thing on
   * screen.
   */
  const [memberSyncMountKey] = useState(newMemberSyncMountKey);
  const memberSyncEntry = useMemberSyncStore(selectMemberSyncEntry(campaignSfdcId));
  const memberSync = memberSyncPhase(memberSyncEntry, memberSyncMountKey);
  const { mutate: syncCampaignMembers } = useTriggerCampaignMemberSync();
  const startMemberSync = useCallback(() => {
    if (!campaignSfdcId) return;
    if (!beginMemberSync(campaignSfdcId, memberSyncMountKey)) return;
    syncCampaignMembers(campaignSfdcId);
  }, [campaignSfdcId, memberSyncMountKey, syncCampaignMembers]);
  useEffect(() => {
    startMemberSync();
  }, [startMemberSync]);
  // For the fallback band's "as of" — same key `ContextCard`/`MembersPanelHeaderStatus`
  // already read, so this is a cache hit, never a second request.
  const { campaign: syncedCampaign } = useLauncherCampaign(campaignSfdcId);
  const rosterAsOf = syncedCampaign?.members_last_synced_at
    ? new Date(syncedCampaign.members_last_synced_at).toLocaleString()
    : null;

  /**
   * Playbooks QA round 4, U10 (P2) — the heading names the play the rep is
   * actually in, so a campaign with several plays isn't three tabs all titled
   * "Play".
   *
   * Reads the SAME `playsQueryKey(campaignSfdcId)` query
   * `PlaybooksHome`/`PlaysActions` already populate (`usePlaysForCampaign`,
   * `staleTime: Infinity`, invalidated on play mutations), so opening a play from the list resolves straight from
   * cache with no extra request; `enabled` keeps playbook-authoring mode (no
   * `playId`, possibly no campaign) from fetching plays at all.
   *
   * `playbookNameById` comes from the library query, merged with the plays
   * read's `playbookNames` (QA round 7, U1, item 3 — `mergePlaybookNames`) so
   * a play whose playbook was ARCHIVED still resolves a real name instead of
   * the raw id, the third location this same F7 gap showed up (Home, the
   * Calendar popover, and here). Arriving from Home, `usePlaysLibrary` is
   * already cached, so this costs no extra request in the common case.
   * `playDisplayName`'s middle fallback uses the merged map. While either
   * query is still in flight the heading stays the plain "Play" —
   * deliberately, rather than flashing the raw id: the id is the right answer
   * for a LIST row (it identifies which row), but a page title that flickers
   * `uuid` -> real name reads as a bug.
   */
  const playsQuery = usePlaysForCampaign(campaignSfdcId, {
    enabled: playId !== undefined && campaignSfdcId !== '',
  });
  const library = usePlaybooksLibrary();
  const playsLibrary = usePlaysLibrary({ enabled: playId !== undefined });
  const play = playsQuery.data?.plays.find((candidate) => candidate.id === playId);
  const playbookNameById = mergePlaybookNames(library.data?.playbooks, playsLibrary.data?.playbookNames);
  const playHeading =
    play === undefined ? 'Play' : `Play · ${playDisplayName(play, playbookNameById)}`;

  /**
   * "Rename play" in the play header's own kebab (2026-09-17 QA follow-ups,
   * U1) — reuses the exact Rename drawer/write Home's `PlaysActions` already
   * has (`usePlayRowActions`), never rebuilt. Patch-in-place onto the
   * campaign-scoped `playsQueryKey(campaignSfdcId)` cache is what
   * flips the heading before the invalidation's refetch lands, mirroring
   * `handlePlaybookRenameSave`'s own shape above.
   */
  const playActions = usePlayRowActions(async (renamed) => {
    queryClient.setQueryData<PlaybooksPlaysResponse>(playsQueryKey(campaignSfdcId), (prev) =>
      prev
        ? { ...prev, plays: prev.plays.map((p) => (p.id === renamed.id ? { ...p, name: renamed.name } : p)) }
        : prev,
    );
    await invalidatePlayLists(queryClient, campaignSfdcId);
  });

  /**
   * R5-10 — playbook builder header: the playbook's own name/last-edited
   * date, from the SAME library query above (`playbookNameById` reads from
   * it too) — zero new requests. `undefined` while the library hasn't
   * resolved yet (or this isn't playbook mode at all), which keeps the
   * heading at the plain "Playbook" fallback below rather than flashing the
   * real name in — the same no-flicker rule `playHeading` documents above.
   */
  const playbookSummary =
    context.kind === 'playbook'
      ? library.data?.playbooks.find((candidate) => candidate.id === playbookId)
      : undefined;
  const playbookHeading = playbookSummary?.name ?? 'Playbook';
  const playbookLastEdited = playbookSummary ? formatEventDate(playbookSummary.updatedAt) : null;

  /**
   * Rename, reached through the header's kebab — same shape as
   * `SequencesStep.tsx#handleRenameSave` (sequence rename): a plain
   * `renamePlaybook` PATCH, patch-in-place on both the `libraryQueryKey()`
   * list AND the `playbookDetailQueryKey(playbookId)` detail cache (the
   * latter is what `QualifyStep`/`SequencesStep`/`ReviewStep` themselves
   * read), then invalidate both — the rename endpoint's response carries no
   * updated timestamp, so invalidating is how the "Last edited" caption
   * above picks up the new value, rather than hand-setting a client-side
   * `new Date().toISOString()`.
   */
  function handlePlaybookRenameSave(name: string) {
    if (playbookId === undefined) return;
    setSavingPlaybookRename(true);
    renamePlaybook(apiFetch, playbookId, name)
      .then(() => {
        queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            playbooks: prev.playbooks.map((p) => (p.id === playbookId ? { ...p, name } : p)),
          };
        });
        queryClient.setQueryData<PlaybooksPlaybookDetailResponse>(
          playbookDetailQueryKey(playbookId),
          (prev) => (prev ? { ...prev, playbook: { ...prev.playbook, name } } : prev),
        );
        void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
        void queryClient.invalidateQueries({ queryKey: playbookDetailQueryKey(playbookId) });
        addToast({ content: `Renamed to "${name}".`, status: 'success' });
        setRenamePlaybookOpen(false);
      })
      .catch(() => {
        addErrorToast({ content: 'Could not rename — try again.' });
      })
      .finally(() => setSavingPlaybookRename(false));
  }

  // Rung-scope is a module-scope store (see rung-scope-store.ts) so it
  // survives QualifyStep unmounting when the Tabs swap away from it — but
  // that means a scope selected on a PREVIOUS Play would otherwise leak
  // into this one. Reset on mount and unmount; StrictMode's dev
  // setup→cleanup→setup double-invoke just resets it twice, which is a
  // no-op (idempotent, same pattern as drawer-store's register/unregister).
  //
  // The waterfall reorder-save lock (`waterfall-save-store.ts`, Codex
  // round-4 Finding #1) is cleared here too, on the same [playId] dependency
  // — a genuine Play change, not a step-tab switch within the same Play. It
  // must NOT be cleared on every step switch (that would defeat the whole
  // point of hoisting it out of `WaterfallCard`'s local state), but it must
  // not leak into a DIFFERENT Play's Playbook either.
  //
  // The playbook DRAFT (`playbook-draft-store.ts`, PR C U3C) is the same
  // shape of module-scope leak risk: `QualifyStep`/`ReviewStep` both seed it
  // via `ensurePlaybookDraft`, which re-seeds ONLY when the store's own
  // `playbookId` doesn't match — the guard that makes it safe to seed from
  // two different step components without racing, but that same guard would
  // otherwise let a PREVIOUS Play's staged ladder survive into this one if
  // this Play happens to route through the same playbook id later. Cleared
  // at the TOP of this effect, not only in cleanup, for the identical
  // StrictMode reason `rung-scope-store.ts`'s own reset idiom documents: a
  // cleanup-only reset leaves the store stuck with whatever the FIRST of
  // StrictMode's dev double-invoked mounts seeded, because the second
  // mount's setup runs before the first mount's cleanup in the
  // setup→cleanup→setup sequence only for effects declared in mount order —
  // resetting at the top of setup as well makes this idempotent regardless.
  //
  // Sequence-copy drafts (`sequence-copy-draft-store.ts`, PR C QA follow-up
  // U9) join the same reset for the same reason, one step stronger: their keys
  // are `${scope}|${sequenceId}|${position}` (`scope` is a rung's `draftKey`,
  // or `'library'` — the D1 shared-edit-leak fix), which still carries NO
  // PLAY identity, so two Plays that map the same Sequence would otherwise
  // share one rep's unsaved typing — the second Play would open showing copy
  // typed against the first, with an "Unsaved changes" marker nobody set. Top
  // of setup AND cleanup, per the paragraph above.
  //
  // AI-generated copy awaiting review (`generated-copy-store.ts`, PR C QA
  // follow-up U12B) joins the same reset for the identical reason as the copy
  // drafts above: its keys are `${scope}|${sequenceId}` (the same D1-style
  // scoping, Codex Issue 2 fix), carrying no PLAY identity, so two Plays
  // mapping the same Sequence would otherwise open onto a generation nobody
  // on this Play asked for.
  //
  // The Sequences step's copy-preview state (`sequence-preview-store.ts`, C3)
  // joins for the same reason once more: `selectedMemberId` is a bare campaign
  // member id, so a member picked on one Play could otherwise be the member the
  // next Play opens its preview on — and `editorMode` would open that Play
  // already in Preview rather than at the prototype's documented `'edit'`
  // default.
  //
  // The Qualify step's members-panel collapse state
  // (`members-panel-collapse-store.ts`, B2) joins for the same reason: it's a
  // bare `qualifyMembersOpen` boolean with no Play or Playbook identity, so an
  // earlier Play left expanded (via its own Preview click) would otherwise
  // open the NEXT Play's Qualify step already expanded instead of the
  // documented collapsed-by-default state.
  //
  // The "Generate with AI" drawer's own session (`generate-with-ai-store.ts`,
  // U6) joins for the identical Play-identity-leak reason: `closeGenerateSession`
  // only resets the drawer's stage/form/names/result, never
  // `generated-copy-store.ts`'s staged review data (which is already cleared,
  // separately, by `clearGeneratedCopyReviews` above) — a session left open
  // across a Play switch would otherwise show the NEXT Play's rung a
  // configure/review/confirm flow that was never opened for it.
  //
  // terra-qyt.14 — keyed on `flowContextKey(context)` rather than `[playId]`:
  // a play and a playbook are different flow-state scopes (see
  // `play-flow-context.ts`'s own doc for why `flowContextKey` deliberately
  // differs from `originPlaceKey`'s unifying convention), and this reset
  // must fire on EITHER identity changing, including a `playId` route
  // swapping to a `playbookId` route or vice versa.
  //
  // ── ROUND-4 U7 (E3): THE UNMOUNT CLEAR IS GONE ─────────────────────────
  // Everything above still holds — a DIFFERENT flow must never open onto the
  // previous flow's state — but the mechanism has moved. Clearing in the
  // CLEANUP made the browser Back button destroy a rep's typed copy with no
  // warning and no way back, and no dialog can prevent that: React Router's
  // popstate handler unmounts this component before any app listener runs,
  // `useBlocker` needs a data router (the app mounts `<BrowserRouter>` at
  // `web/app-home/src/main.tsx`), and a sentinel-history workaround
  // false-fires on every Back through a `?step=` entry.
  //
  // So the clear is now keyed on the flow context ACTUALLY CHANGING, tracked
  // across unmounts in `flow-session-store.ts` rather than by this
  // component's lifecycle: `shouldClearFlowStores(prev, next)` is true only
  // when a previous flow was recorded AND it differs from this one. Leaving
  // (Back, a link, a close) clears nothing; re-entering the same flow finds
  // `prev === next` and clears nothing; opening a different play or playbook
  // clears exactly the list it always did. StrictMode's dev
  // setup→cleanup→setup is the `prev === next` case, so this stays
  // idempotent without any ref bookkeeping.
  //
  // The cleanup keeps exactly two calls, and both are deliberate:
  // `clearWaterfallSaveStore` releases an in-flight reorder-save LOCK (a
  // lock left set would wedge the next mount, and it is not draft data), and
  // `closeGenerateSession` resets the AI drawer's OPEN STATE only — the
  // staged results it reviews live in `generated-copy-store` and now
  // survive, which is the point. Nothing else may be added back here.
  //
  // Codex fixes 2026-09-17, F2 — anything that must reopen a Generate
  // session on THIS flow after a context change (the usage sidebar's
  // `'resume-review'`) waits for `recordFlowSession` below via
  // `!shouldClearFlowStores(lastContextKey, contextKey)` (`SequencesStep`) —
  // this effect is a parent effect and runs after the children's, so a
  // child that opened a session in the same commit would have it closed
  // here. Do not move the resume into this effect; the gate is the
  // contract.
  const contextKey = flowContextKey(context);
  const flowLabel = context.kind === 'play' ? 'Play' : 'Playbook';
  const returnHref = `${location.pathname}${location.search}`;
  const hasUnsavedWork = useHasUnsavedWork();

  useEffect(() => {
    if (shouldClearFlowStores(useFlowSessionStore.getState().lastContextKey, contextKey)) {
      clearRungScope();
      clearPlaybookDraft();
      clearSequenceCopyDrafts();
      clearGeneratedCopyReviews();
      closeGenerateSession();
      clearSequencePreview();
      clearMembersPanelCollapse();
      clearMemberTests();
      clearReviewSelection();
      clearDispatchLogFilters();
      clearReviewCards();
      clearLadderAdoptionSignal();
    }
    recordFlowSession(contextKey, returnHref, flowLabel);
    return () => {
      clearWaterfallSaveStore();
      closeGenerateSession();
    };
    // `returnHref`/`flowLabel` are deliberately NOT dependencies — this
    // effect owns the CLEAR, which must fire on an identity change only. The
    // href is kept fresh by the tiny effect below instead, so a `?step=`
    // switch never re-runs the clear check.
  }, [contextKey]);

  // Keeps `UnsavedEditsPill`'s "take me back" target on the step the rep was
  // actually on. Guarded on `contextKey` inside the store action, so an
  // unmounting shell's trailing run can never overwrite the href of the flow
  // that replaced it.
  useEffect(() => {
    updateFlowReturnHref(contextKey, returnHref);
  }, [contextKey, returnHref]);

  function goToStep(next: PlayFlowStepId) {
    const params = new URLSearchParams(searchParams);
    params.set('step', playFlowStepParam(next));
    setSearchParams(params, { replace: false });
  }

  /**
   * terra-qyt.15 (U10) — playbook mode's seed-campaign write. `replace: true`
   * (deliberately unlike `goToStep`'s `replace: false` above) so swapping the
   * seed mid-session does not stack a new history entry per pick — the seed
   * is a live filter on this Playbook view, not a navigation the rep would
   * ever want to "back" through one pick at a time. `clearRungScope()` runs
   * because a Preview scoped against the OLD campaign's snapshot is
   * meaningless once the seed changes.
   *
   * terra-qyt round-2 fix U18 (W1, Design decision 1 — "clear on seed
   * change, not key-by-campaign") — `clearGeneratedCopyReviews()` and
   * `closeGenerateSession()` ALSO run here, unlike the playbook draft and
   * hand-typed copy drafts (`sequence-copy-draft-store.ts`), which stay keyed
   * by rung/sequence and survive a seed swap: those are campaign-neutral
   * (the same draft mapping/hand-typed copy applies no matter which campaign
   * the playbook is being measured against). A generated review is NOT
   * campaign-neutral — it was written FROM one campaign's context
   * (`generation-context`'s roster/description), so a review generated for
   * campaign A is not a review of campaign B just because it sits on the
   * same rung. Dropping it on seed change is the cheap, unconditional fix;
   * `GenerateWithAIDrawer.tsx`'s `hostCampaignSfdcId` check is the
   * belt-and-suspenders backstop for anything that slips past this (a
   * session opened in the same tick the seed changes, a future caller that
   * forgets to wire this reset).
   *
   * terra-qyt round-3 fix U24 (W1 residual, Design decision 7) —
   * `closeUsageSidebarForSeedChange` is the FOURTH campaign-bound artefact
   * this reset drops: a save Apply parked in the persistent usage sidebar
   * (`usage-sidebar-store.ts`) is subject to the exact same staleness as a
   * staged generated review — it was written FROM the OLD seed's context —
   * so it cannot survive a seed swap either. Hand-typed pendings (no AI
   * provenance) survive for the same reason hand-typed copy drafts do: they
   * are campaign-neutral. Only fires when `playbookId` is defined —
   * `handleSeedChange` is only ever wired in playbook mode.
   */
  function handleSeedChange(nextCampaignSfdcId: string | null) {
    const params = new URLSearchParams(searchParams);
    if (nextCampaignSfdcId === null) {
      params.delete('campaignSfdcId');
    } else {
      params.set('campaignSfdcId', nextCampaignSfdcId);
    }
    setSearchParams(params, { replace: true });
    clearRungScope();
    clearGeneratedCopyReviews();
    closeGenerateSession();
    if (playbookId !== undefined) closeUsageSidebarForSeedChange(playbookId);
  }

  // terra-qyt.14 — play-mode-only: a playbook route has no `playId` to be
  // missing (`context.kind === 'playbook'` never even reaches here with an
  // empty identity — `flowContextFromRoute` throws before this component
  // mounts rather than rendering it with a blank id).
  if (context.kind === 'play' && !playId) {
    return (
      <div className="p-6">
        <Text size="sm" variant="critical">
          Missing playId.
        </Text>
      </div>
    );
  }

  // The steps wait for THIS mount's live member sync to settle (success or
  // failure) — every member-derived read inside them (roster, coverage,
  // ladder counts, review tally) is over the mirror the sync refreshes. Same
  // rule in both play and playbook mode; with no campaignSfdcId bound yet
  // (an unseeded playbook) there is nothing to sync, so steps mount as before.
  //
  // Plan 2026-09-27-006 U3 — there is no legacy-play snapshot gate any more:
  // every play is pinned at create (and by the U1 backfill), so the editors
  // read the play's own ladder from their first read.
  const shouldMountSteps = !campaignSfdcId || memberSync.kind !== 'syncing';

  // Built once here and handed to each step as a slot (see module doc) —
  // `null` when there's no campaignSfdcId (in play mode the error band below
  // renders instead; in playbook mode with no seed, the steps simply render
  // with no members panel and em-dash counts — see the Resolved note in
  // terra-qyt.14's bead description).
  // Play isolation (plan 2026-09-25-002, U5) — `playId` (undefined on the
  // playbook doorway) so the panel's ladder/playbook reads bucket by this
  // play's own ladder; its gap check's sequence read never varies by play.
  const membersPanel = campaignSfdcId ? <MembersPanel campaignSfdcId={campaignSfdcId} playId={playId} /> : null;
  // Batch 7 / Unit E1 — Qualify hosts this panel inside its OWN
  // `CollapsibleCard`, so it mounts the `embedded` variant (drops the
  // panel's own border/padding/heading; the host header carries the
  // matched-count/previewing cluster instead, via `MembersPanelHeaderStatus`
  // — see `MembersPanel.tsx`'s module doc). Sequences/Review keep the plain
  // `membersPanel` above — their bare, non-collapsible mount is unaffected.
  const qualifyMembersPanel = campaignSfdcId ? (
    <MembersPanel campaignSfdcId={campaignSfdcId} playId={playId} embedded />
  ) : null;

  return (
    <>
      <Tabs
        value={step}
        onValueChange={(value) => goToStep(parsePlayFlowStep(String(value)))}
        className="flex flex-1 flex-col"
      >
      <div className="flex flex-col gap-3 border-b border-border-primary px-6 py-4">
        {/*
          U7 (E3) — renders nothing; it installs the beforeunload prompt and
          the capture-phase header-link interception for as long as this flow
          holds unsaved work. Mounted inside the shell (not the layout) so it
          is scoped to the editing surface, and beneath `PlaybooksLayout`'s
          `GlazeModalProvider`, which `useAlertDialog` requires.
        */}
        <UnsavedWorkGuard dirty={hasUnsavedWork} />
        {context.kind === 'play' ? (
          <div className="flex items-center justify-between gap-2">
            <Heading size="lg">{playHeading}</Heading>
            {/*
              U1 (2026-09-17) — "Rename play" only: deleting the play you are
              standing in is Home's job (it would strand the route).
            */}
            <KebabMenu
              ariaLabel={`More actions for ${playHeading}`}
              items={
                play
                  ? playActions
                      .menuItemsFor(play, playDisplayName(play, playbookNameById))
                      .filter((item) => item.key === 'rename')
                      .map((item) => ({ ...item, label: 'Rename play' }))
                  : []
              }
            />
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              <Heading size="lg">{playbookHeading}</Heading>
              {playbookLastEdited && (
                <Text size="xs" variant="tertiary">
                  Last edited {playbookLastEdited}
                </Text>
              )}
            </div>
            <KebabMenu
              ariaLabel={`More actions for ${playbookHeading}`}
              items={
                playbookSummary
                  ? [{ key: 'rename', label: 'Rename', onSelect: () => setRenamePlaybookOpen(true) }]
                  : []
              }
            />
          </div>
        )}
        <TabList>
          {PLAY_FLOW_STEPS.map((s) => (
            <Tab key={s.id} value={s.id}>
              {s.label}
            </Tab>
          ))}
          {/*
            Batch 9 B1 — "Audience Rules" as a DISABLED 4th tab, "Coming
            soon" tooltip. Known Glaze/Base UI gotcha: a `disabled` Tab
            renders a `disabled` <button>, and disabled form controls
            suppress pointer events (no hover, so no `mouseenter`) — the
            tooltip's `TooltipTrigger` would never see the hover that opens
            it if the Tab itself were the trigger. Wrapping the Tab in a
            `span` and making that span the trigger sidesteps this: the span
            isn't disabled, so it still receives hover/focus, while the Tab
            inside it stays visually and functionally disabled (unclickable,
            not part of tab-order navigation to a real panel).
            NEEDS A LIVE BROWSER CHECK (Spencer) — see notes file; not
            blocking this unit.
          */}
          {COMING_SOON_TABS.map((tab) => (
            <Tooltip key={tab.id}>
              <TooltipTrigger>
                <span tabIndex={0} className="inline-flex">
                  <Tab value={tab.id} disabled>
                    {tab.label}
                  </Tab>
                </span>
              </TooltipTrigger>
              <TooltipPanel>Coming soon</TooltipPanel>
            </Tooltip>
          ))}
        </TabList>
      </div>

      {context.kind === 'play' && !campaignSfdcId && (
        <div className="border-b border-border-primary px-6 py-4">
          <Text size="sm" variant="critical">
            Missing campaignSfdcId — open this Play from the campaign home page.
          </Text>
        </div>
      )}

      {!!campaignSfdcId && memberSync.kind === 'syncing' && (
        <div className="flex flex-col items-center gap-2 px-6 py-12" role="status" aria-live="polite">
          <LoadingSpinner size="md" />
          <Text size="sm">Syncing the latest campaign members from Salesforce…</Text>
          <Text size="xs" variant="tertiary">
            This usually takes a few seconds. The roster, coverage and group previews load once it finishes.
          </Text>
        </div>
      )}

      {!!campaignSfdcId && memberSync.kind === 'failed' && (
        <div className="border-b border-border-primary px-6 py-3">
          <Alert status="warning">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Text size="sm">
                Couldn&apos;t refresh this campaign&apos;s members from Salesforce — showing the roster
                {rosterAsOf ? ` as of ${rosterAsOf}` : ' from the last sync'}.
                {memberSync.detail ? ` ${memberSync.detail}` : ''}
              </Text>
              <Button variant="secondary" size="sm" onClick={startMemberSync}>
                Try again
              </Button>
            </div>
          </Alert>
        </div>
      )}

      <TabPanels render={<div className="flex-1" />}>
        <TabPanel value="qualify">
          {shouldMountSteps ? (
            <QualifyStep
              campaignSfdcId={campaignSfdcId}
              playId={playId}
              playbookId={playbookId}
              onGoToSequences={() => goToStep('sequences')}
              membersPanel={qualifyMembersPanel}
              preselectRungId={preselectRungId}
              onSeedChange={handleSeedChange}
            />
          ) : null}
        </TabPanel>
        <TabPanel value="sequences">
          {shouldMountSteps ? (
            <SequencesStep
              campaignSfdcId={campaignSfdcId}
              playId={playId}
              playbookId={playbookId}
              onGoToReview={() => goToStep('review')}
              membersPanel={membersPanel}
              preselectRungId={preselectRungId}
            />
          ) : null}
        </TabPanel>
        <TabPanel value="review">
          {shouldMountSteps ? (
            <ReviewStep
              campaignSfdcId={campaignSfdcId}
              playId={playId}
              playbookId={playbookId}
              onGoToQualify={() => goToStep('qualify')}
              onGoToSequences={() => goToStep('sequences')}
            />
          ) : null}
        </TabPanel>
        {/*
          Unreachable: the tab above is `disabled`, and `?step=audience-rules`
          falls back to `qualify` (`parsePlayFlowStep`) rather than resolving
          here, so `step` can never equal `tab.id` and Base UI's `Tabs.Panel`
          never shows this. Mounted anyway so `AudienceRulesStep` stays
          type-checked and one line away from being wired up for real.
        */}
        {COMING_SOON_TABS.map((tab) => (
          <TabPanel key={tab.id} value={tab.id}>
            <AudienceRulesStep />
          </TabPanel>
        ))}
      </TabPanels>
      </Tabs>
      {renamePlaybookOpen && playbookSummary && (
        <PushDrawer title={`Rename ${playbookSummary.name}`} onClose={() => setRenamePlaybookOpen(false)}>
          <NameDrawerField
            key={playbookSummary.id}
            initialName={playbookSummary.name}
            saving={savingPlaybookRename}
            onSave={handlePlaybookRenameSave}
            onCancel={() => setRenamePlaybookOpen(false)}
          />
        </PushDrawer>
      )}
      {playActions.renameDrawer}
    </>
  );
}
